import type { LabEnterpriseInvestigation, LabHospitalInvestigationLookup } from '@/modules/lab/api/labOperationalMasterApi';
import { isBillingPending } from '@/modules/lab/catalog/api/catalogPilotHardeningApi';
import {
  getAliasesForMaster,
  isLocalInvestigationCode } from

'@/modules/lab/catalog/catalogPilotMetaStore';

export type TaxonomyNode = {
  id: string;
  label: string;
  kind: 'root' | 'category';
  count: number;
  enabledCount: number;
  children?: TaxonomyNode[];
};

export type BillingLinkStatus = 'LINKED' | 'PENDING' | 'MISSING';

export type CatalogInvestigationRow = {
  investigationMasterId: string;
  investigationCode: string;
  displayName: string;
  category: string;
  specimenType: string | null;
  billableFlag: boolean;
  activeFlag: string;
  mappingId: string | null;
  hospitalEnabled: boolean;
  billingServiceId: string | null;
  billingLinkStatus: BillingLinkStatus;
  tariffAmount: number | null;
  localDisplayName: string | null;
  localCode: string | null;
  /** Hospital-only test (LOCAL-* code convention) */
  isLocal: boolean;
  /** Inactive enterprise row pending activation */
  isDraft: boolean;
  aliases: string[];
};

export type ImportRowDraft = {
  id: string;
  rowNumber: number;
  rawCode: string;
  rawName: string;
  rawCategory: string;
  matchStatus: 'EXACT' | 'FUZZY' | 'NEW' | 'CONFLICT' | 'SKIP';
  confidence: number;
  suggestedMasterId: string | null;
  suggestedCode: string | null;
  suggestedName: string | null;
  resolution: 'LINK' | 'CREATE' | 'SKIP' | null;
  applyStatus?: string;
  errorMessage?: string | null;
};

export type ImportBatchState = {
  id: string;
  fileName: string;
  createdAt: string;
  batchStatus?: string;
  appliedRows?: number;
  failedRows?: number;
  skippedRows?: number;
  rows: ImportRowDraft[];
  columnMapping: Record<string, string>;
};

/** Placeholder billing service id until hospital tariff mapping is complete. */
export const BILLING_LINK_PENDING = 'LAB-CATALOG-PENDING';
/** @deprecated Use BILLING_LINK_PENDING */
export const PILOT_BILLING_PLACEHOLDER = BILLING_LINK_PENDING;

function resolveBillingLinkStatus(
hospitalEnabled: boolean,
billingServiceId: string | null | undefined)
: BillingLinkStatus {
  if (!hospitalEnabled) return 'MISSING';
  if (isBillingPending(billingServiceId)) return 'PENDING';
  return 'LINKED';
}

export function mergeCatalogRows(
enterprise: LabEnterpriseInvestigation[],
hospital: LabHospitalInvestigationLookup[],
serverAliases?: Record<string, string[]>)
: CatalogInvestigationRow[] {
  const byMaster = new Map(hospital.map((h) => [h.investigationMasterId, h]));

  return enterprise.map((e) => {
    const m = byMaster.get(e.investigationMasterId);
    const isLocal = isLocalInvestigationCode(e.investigationCode);
    const isDraft = e.activeFlag === 'I';
    const billingServiceId = m?.billingServiceId ?? null;
    const hospitalEnabled = m?.enabled ?? false;
    const aliases =
    serverAliases?.[e.investigationMasterId] ??
    getAliasesForMaster(e.investigationMasterId);
    return {
      investigationMasterId: e.investigationMasterId,
      investigationCode: e.investigationCode,
      displayName: e.displayName,
      category: e.category,
      specimenType: e.specimenType ?? null,
      billableFlag: e.billableFlag,
      activeFlag: e.activeFlag,
      mappingId: m?.mappingId ?? null,
      hospitalEnabled,
      billingServiceId,
      billingLinkStatus: resolveBillingLinkStatus(hospitalEnabled, billingServiceId),
      tariffAmount: m?.tariffAmount ?? null,
      localDisplayName: m?.localDisplayName ?? null,
      localCode: m?.localCode ?? null,
      isLocal,
      isDraft,
      aliases
    };
  });
}